import { Injectable } from '@angular/core';
import { TrackingChannel } from '../model/enums/tracking-channel';
import { ClientInfo } from '../model/interfaces/client-info';
import { SplittestValuePair } from '../model/interfaces/splittest-value-pair';
import { TrackingMetadataValuePair } from '../model/interfaces/tracking-metadata-value-pair';

@Injectable()
export class ClientService
{
    public versionName: string;

    private clientInfo: ClientInfo;
    private referrer: string;
    private browser: string;
    private platform: string;
    private version: string;
    private trackingChannel: TrackingChannel;
    private trackingChannelMetadata: object;
    private trackingMetadataValuePair: TrackingMetadataValuePair = {"Name": "", "Value": ""};
    private splittestValuePair: SplittestValuePair = {"Name": "", "Segment": ""};

    public constructor()
    {
        this.initialize();
    }

    public getClientInfo(): ClientInfo
    {
        this.trackingChannel = this.getTrackingChannelFromSessionStorage();
        this.clientInfo = {
            Referrer: this.referrer,
            Browser: this.browser,
            Platform: this.platform,
            Version: this.version,
            TrackingChannel: this.trackingChannel,
            TrackingChannelMetadata: this.trackingChannelMetadata,
            TrackingMetadataValuePair: this.trackingMetadataValuePair,
            SplittestValuePair: this.splittestValuePair
        };

        return this.clientInfo;
    }

    private initialize(): void
    {
        this.referrer = document.referrer;
        this.browser = this.getBrowser();
        this.platform = this.getPlatformName();
        this.version = this.getBrowserVersion(this.browser);
        this.detectSpecificLeadFamlyLandingpage();
        this.detectSpecificDanskeSpilReference();
        this.trackingChannelMetadata = this.initializeTrackingChannelMetaData();
        this.trackingMetadataValuePair = this.initializeTrackingMetaData();
    }

    public initializeTrackingChannelMetaData(): object
    {
        const valueFromSessionStorage = sessionStorage.getItem('clientInfo');
        const convertedSessionStorageValue = valueFromSessionStorage === null ? {} : JSON.parse(valueFromSessionStorage);

        if(convertedSessionStorageValue.TrackingChannelMetadata){
            return convertedSessionStorageValue.TrackingChannelMetadata;
        }
        else{
            return {}
        }
    }

    public initializeTrackingMetaData(): TrackingMetadataValuePair
    {
        const valueFromSessionStorage = sessionStorage.getItem('clientInfo');
        const convertedSessionStorageValue = valueFromSessionStorage === null ? {} : JSON.parse(valueFromSessionStorage);

        if(convertedSessionStorageValue.TrackingMetadataValuePair){
            return convertedSessionStorageValue.TrackingMetadataValuePair;
        }
        else{
            return null;
        }
    }

    public addTrackingChannelMetaData(key: string, value)
    {
        this.trackingChannelMetadata[key] = value;
    }

    public removeTrackingChannelMetaData(key: string)
    {
        if(this.trackingChannelMetadata[key])
        {
            delete this.trackingChannelMetadata[key];
        }
    }

    public addTrackingMetaData(key: string, value)
    {
        this.trackingMetadataValuePair[key] = value;
    }

    public removeTrackingMetaData(key: string)
    {
        if(this.trackingChannelMetadata[key])
        {
            delete this.trackingMetadataValuePair[key];
        }
    }

    public addSplitTestMetaData(key: string, value)
    {
        this.splittestValuePair[key] = value;
    }

    public removeSplitTestMetaData(key: string)
    {
        if(this.splittestValuePair[key])
        {
            delete this.splittestValuePair[key];
        }
    }

    // Check for desktop device
    public checkForDesktopView(): boolean
    {
        const windowWidth = window.innerWidth;
        if (windowWidth > 1024)
        {
            return true;
        }
        else
        {
            return false;
        }
    }

    // Check for tablet view
    public checkForTabletView(): boolean
    {
        const windowWidth = window.innerWidth;
        if (windowWidth < 768)
        {
            return true;
        }
        else
        {
            return false;
        }
    }

    // Check for mobile devices ONLY
    public checkForMobileView(): boolean
    {
        const windowWidth = window.innerWidth;
        if (windowWidth < 575)
        {
            return true;
        }
        else
        {
            return false;
        }
    }

    public detectSpecificLeadFamlyLandingpage(): void
    {
        const lflp = this.getUrlParameter('utm_term') || null;
        
        if(lflp !== null)
        {
            if(lflp === "LP1")
            {
                this.addTrackingMetaData('Name', "LeadFamly landingpage");
                this.addTrackingMetaData('Value', "LP1");
            }
            if(lflp === "LP2")
            {
                this.addTrackingMetaData('Name', "LeadFamly landingpage");
                this.addTrackingMetaData('Value', "LP2");
            }
        }
        
        var clientInfo = this.getClientInfo();
        sessionStorage.setItem('clientInfo', JSON.stringify(clientInfo));
    }

    public detectSpecificDanskeSpilReference(): void
    {
        const trafficFromDanskeSpil = this.getUrlParameter('dsmeta') || null;
        
        if(trafficFromDanskeSpil !== null)
        {
            if(trafficFromDanskeSpil === "klasselotterietsubpage")
            {
                this.addTrackingMetaData('Name', "referrer-path");
                this.addTrackingMetaData('Value', "danskespil.dk/klasselotteriet");
            }
           
        }
        
        var clientInfo = this.getClientInfo();
        sessionStorage.setItem('clientInfo', JSON.stringify(clientInfo));
    }

    private getUrlParameter(name) 
    {
        name = name.replace(/\\/g, '\\\\').replace(/\[/g, '\\[').replace(/\]/g, '\\]');
        var regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
        var results = regex.exec(location.search);
        return results === null ? null : decodeURIComponent(results[1].replace(/\+/g, ' '));
    }

    public getCoid(): number
    {
        const valueFromSessionStorage = sessionStorage.getItem('trackingChannel');
        const convertedSessionStorageValue = valueFromSessionStorage === null ? {} : JSON.parse(valueFromSessionStorage);

        let coid = 1;

        if(convertedSessionStorageValue.coid ){
            coid= parseInt(convertedSessionStorageValue.coid, 10);
        }

        return coid;

    }

    private getBrowser(): string
    {
        let browserName = 'Ukendt Browser';

        if (navigator.userAgent.indexOf('Chrome') < navigator.userAgent.indexOf('Safari'))
        { browserName = 'Chrome'; }
        if (navigator.userAgent.indexOf('Safari') > -1 && navigator.userAgent.indexOf('Chrome') === -1)
        { browserName = 'Safari'; }
        if (navigator.userAgent.indexOf('Firefox') > -1)
        { browserName = 'Firefox'; }
        if (navigator.userAgent.indexOf('Trident') > -1)
        { browserName = 'Explorer'; }
        if ((navigator.userAgent.indexOf('Edge') > navigator.userAgent.indexOf('Chrome')) && (navigator.userAgent.indexOf('Edge') > navigator.userAgent.indexOf('Safari')))
        { browserName = 'Edge'; }
        if (navigator.userAgent.indexOf('Samsung') > -1)
        { browserName = 'Samsung'; }

        return browserName;
    }

    public getPlatformName(): string
    {
        let platformName = 'Ukendt Platform';
        if (navigator.appVersion.indexOf('Win') > -1)
        { platformName = 'Windows'; }
        if (navigator.appVersion.indexOf('Mac') > -1)
        { platformName = 'MacOS'; }
        if (navigator.appVersion.indexOf('iPad') > -1)
        { platformName = 'iPad'; }
        if (navigator.appVersion.indexOf('iPhone') > -1)
        { platformName = 'iPhone'; }
        if (navigator.appVersion.indexOf('Android') > -1)
        { platformName = 'Android'; }

        return platformName;
    }

    private getBrowserVersion(browserName: string): string
    {
        this.versionName = 'Ukendt Version';
        const appVersion = navigator.userAgent;
        let versionSubstring: string;

        if (browserName === 'Chrome')
        {
            if (appVersion.includes('Mobile'))
            {
                versionSubstring = appVersion.substring(appVersion.indexOf('Chrome') + 7);
                this.versionName = versionSubstring.slice(0, -30);
            }
            else
            {
                versionSubstring = appVersion.substring(appVersion.indexOf('Chrome') + 7);
                this.versionName = versionSubstring.slice(0, -14);
            }
        }
        if (browserName === 'Safari')
        {
            this.versionName = appVersion.substring(appVersion.indexOf('Safari') + 7);
        }
        if (browserName === 'Firefox')
        {
            this.versionName = appVersion.substring(appVersion.indexOf('Firefox') + 8);
        }
        if (browserName === 'Explorer')
        {
            this.versionName = appVersion.substring(appVersion.indexOf('MSIE') + 5);
        }
        if (browserName === 'Edge')
        {
            this.versionName = appVersion.substring(appVersion.indexOf('Edge') + 5);
        }
        if (browserName === 'Samsung')
        {
            this.versionName = appVersion.substring(appVersion.indexOf('Samsung') + 7);
        }
        return this.versionName;
    }

    public sliceRefererValue(refererValue: string): string
    {
        const referer = refererValue;
        const slicedRefererValue = referer.split('?')[0];
        return slicedRefererValue;
    }

    public isMobile(): boolean
    {
        const re = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;
        return re.test(navigator.userAgent);
    }

    public getTrackingChannelFromSessionStorage(): TrackingChannel
    {
        const valueFromSessionStorage = sessionStorage.getItem('trackingChannel');
        const convertedSessionStorageValue = valueFromSessionStorage === null ? {} : JSON.parse(valueFromSessionStorage);

        if (convertedSessionStorageValue.referrer)
        {
            this.referrer = this.sliceRefererValue(convertedSessionStorageValue.referrer);
        }

        // lftrackingchannel exist
        if (convertedSessionStorageValue.lftrackingchannel !== null)
        {
            if (convertedSessionStorageValue.lftrackingchannel === 'Direct')
            {
                return TrackingChannel.Direct;
            }
            if (convertedSessionStorageValue.lftrackingchannel === 'OrganicSearch')
            {
                return TrackingChannel.OrganicSearch;
            }
            if (convertedSessionStorageValue.lftrackingchannel === 'Email')
            {
                return TrackingChannel.Email;
            }
            if (convertedSessionStorageValue.lftrackingchannel === 'PaidSearch')
            {
                return TrackingChannel.PaidSearch;
            }
            if (convertedSessionStorageValue.lftrackingchannel === 'PaidSocial')
            {
                return TrackingChannel.PaidSocial;
            }
            if (convertedSessionStorageValue.lftrackingchannel === 'Display')
            {
                return TrackingChannel.Display;
            }
            if (convertedSessionStorageValue.lftrackingchannel === 'Affiliate')
            {
                return TrackingChannel.Affiliate;
            }
            if (convertedSessionStorageValue.lftrackingchannel === 'OtherDigital')
            {
                return TrackingChannel.OtherDigital;
            }
        }

        // All properties from session storage are null and referrer value is empty string
        if (convertedSessionStorageValue.gclid === null && 
            convertedSessionStorageValue.msclkid === null && 
            convertedSessionStorageValue.fbclid === null && 
            convertedSessionStorageValue.snapid === null &&
            convertedSessionStorageValue.medium === null && 
            convertedSessionStorageValue.source === null && 
            convertedSessionStorageValue.coid === null && 
            convertedSessionStorageValue.referrer === '')
        {
            return TrackingChannel.Direct;
        }

        // All properties from session storage are null and referrer value is different from empty string
        if (convertedSessionStorageValue.gclid === null && 
            convertedSessionStorageValue.msclkid === null && 
            convertedSessionStorageValue.fbclid === null && 
            convertedSessionStorageValue.snapid === null &&
            convertedSessionStorageValue.medium === null && 
            convertedSessionStorageValue.source === null && 
            convertedSessionStorageValue.coid === null && 
            convertedSessionStorageValue.referrer !== '')
        {
            // Check if referrer string contains identifiers and return relevant trackingchannel
            // We need to do this for users that navigate our site by opening a new tab in the browser
            if (convertedSessionStorageValue.referrer.includes('coid'))
            {
                return TrackingChannel.Affiliate;
            }
            if (convertedSessionStorageValue.referrer.includes('utm_medium=email'))
            {
                return TrackingChannel.Email;
            }
            if (convertedSessionStorageValue.referrer.includes('gclid') || 
                convertedSessionStorageValue.referrer.includes('msclkid'))
            {
                return TrackingChannel.PaidSearch;
            }
            if (convertedSessionStorageValue.referrer.includes('fbclid') || 
                convertedSessionStorageValue.referrer.includes('snapid') ||
                convertedSessionStorageValue.referrer.includes('utm_medium=social') || 
                convertedSessionStorageValue.referrer.includes('utm_source=facebook'))
            {
                return TrackingChannel.PaidSocial;
            }
            if (convertedSessionStorageValue.referrer.includes('utm_medium=banner'))
            {
                return TrackingChannel.Display;
            }
            // This check need to be at bottom because navigating our site by opening a new tab in the browser always includes ddkweb or klasselotteriet
            if (convertedSessionStorageValue.referrer.includes('ddkweb') || 
                convertedSessionStorageValue.referrer.includes('klasselotteriet')) 
            {
                return TrackingChannel.OtherDigital;
            }

            return TrackingChannel.OrganicSearch;
        }

        if (convertedSessionStorageValue.coid !== null)
        {
            return TrackingChannel.Affiliate;
        }
        // utm_medium exist
        if (convertedSessionStorageValue.medium !== null)
        {
            if (convertedSessionStorageValue.medium === 'social' || 
                convertedSessionStorageValue.medium === 'email' || 
                convertedSessionStorageValue.medium === 'banner')
            {
                return this.convertMediumStringToTrackingChannelEnum(convertedSessionStorageValue.medium);
            }
            // return this.convertMediumStringToTrackingChannelEnum(convertedSessionstorageValue.medium);
        }
        // utm_source exist
        if (convertedSessionStorageValue.source !== null)
        {
            return this.convertSourceStringToTrackingChannelEnum(convertedSessionStorageValue.source);
        }

        // Google click identifier or Microsoft click identifier exists
        if (convertedSessionStorageValue.gclid !== null || convertedSessionStorageValue.msclkid !== null)
        {
            return TrackingChannel.PaidSearch;
        }
        if (convertedSessionStorageValue.fbclid !== null)
        {
            return TrackingChannel.PaidSocial;
        }
        if (convertedSessionStorageValue.snapid !== null) {
            return TrackingChannel.PaidSocial;
        }

    }

    private convertMediumStringToTrackingChannelEnum(trackingchannelstring: string): TrackingChannel
    {
        if (!trackingchannelstring || trackingchannelstring === null)
        {
            return TrackingChannel.OtherDigital;
        }
        if (trackingchannelstring.toLowerCase() === 'social')
        {
            return TrackingChannel.PaidSocial;
        }
        if (trackingchannelstring.toLowerCase() === 'email')
        {
            return TrackingChannel.Email;
        }
        if (trackingchannelstring.toLowerCase() === 'banner')
        {
            return TrackingChannel.Display;
        }

        return TrackingChannel.OtherDigital;
    }

    private convertSourceStringToTrackingChannelEnum(trackingchannelstring: string): TrackingChannel
    {
        if (!trackingchannelstring || trackingchannelstring === null)
        {
            return TrackingChannel.OtherDigital;
        }
        if (trackingchannelstring.toLowerCase() === 'facebook')
        {
            return TrackingChannel.PaidSocial;
        }

        return TrackingChannel.OtherDigital;
    }


}
